export type VestingGraphIntroPhase = 'done' | 'pending' | 'playing';

/** Target length of the left-to-right wave on a dense monthly chart. */
export const VESTING_GRAPH_INTRO_WAVE_MS = 720;
/** How long each bar / column takes to grow once its beat in the wave starts. */
export const VESTING_GRAPH_INTRO_BAR_MS = 560;
/** Fade + rise for badges, brackets, and axis ticks. */
export const VESTING_GRAPH_INTRO_LABEL_MS = 420;
/** Labels trail the series slightly so they appear as that slice of the plot arrives. */
export const VESTING_GRAPH_INTRO_LABEL_OFFSET_MS = 90;
/** Pause after layout is measured so the first frame is not a zero-width plot. */
export const VESTING_GRAPH_INTRO_START_DELAY_MS = 80;
export const VESTING_GRAPH_INTRO_BAR_STAGGER_MIN_MS = 18;
export const VESTING_GRAPH_INTRO_BAR_STAGGER_MAX_MS = 88;
export const VESTING_GRAPH_INTRO_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Dense monthly charts spread the wave across WAVE_MS; sparse milestone charts keep a
 * wider gap between columns so each step of the staircase is readable.
 */
export const resolveVestingGraphIntroWaveMs = (pointCount: number) => {
  if (pointCount <= 1) return 0;

  const staggerMs = clamp(
    VESTING_GRAPH_INTRO_WAVE_MS / (pointCount - 1),
    VESTING_GRAPH_INTRO_BAR_STAGGER_MIN_MS,
    VESTING_GRAPH_INTRO_BAR_STAGGER_MAX_MS,
  );

  return (pointCount - 1) * staggerMs;
};

export const resolveVestingGraphBarIntroDelayMs = (index: number, pointCount: number) => {
  if (index <= 0 || pointCount <= 1) return 0;

  return (index / (pointCount - 1)) * resolveVestingGraphIntroWaveMs(pointCount);
};

export const resolveVestingGraphRevealDelayMs = (leftPercent: number, pointCount: number) => {
  const t = clamp(leftPercent, 0, 100) / 100;

  return VESTING_GRAPH_INTRO_LABEL_OFFSET_MS + t * resolveVestingGraphIntroWaveMs(pointCount);
};

export const resolveVestingGraphAreaIntroMs = (pointCount: number) =>
  resolveVestingGraphIntroWaveMs(pointCount) + VESTING_GRAPH_INTRO_BAR_MS;

export const resolveVestingGraphIntroPlayingMs = (pointCount: number) =>
  resolveVestingGraphIntroWaveMs(pointCount) +
  Math.max(VESTING_GRAPH_INTRO_BAR_MS, VESTING_GRAPH_INTRO_LABEL_MS) +
  VESTING_GRAPH_INTRO_LABEL_OFFSET_MS;
